import React, { useEffect, useState } from 'react';
import { browserHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Sticky from 'react-stickynode';

import SliderQuestion from './SliderQuestion';
import TextQuestion from './TextQuestion';
import CustomTextQuestion from './CustomTextQuestion';
import NavButtons from './NavButtons';
import Loading from './Loading';
import StatusModal from './StatusModal';
import Error from './Error';
import { getAvailablePoints, getSpentPoints } from '../selectors/answers.selectors';

import scrollTop from '../utils/scrollTop';
import 'font-awesome/css/font-awesome.css';
import Comment from './Comment';
import iconOk from '../styles/images/icon-ok.png';
import {
  checkboxChanged,
  customTextChanged,
  customTextCheckboxChanged,
  postAnswers,
  postCustomTextAnswers,
  postTextAnswers,
  saveAnswers,
  textAnswerError,
  textChanged,
} from '../actions/answers';
import { showStatusModal } from '../actions/navigation';
import { saveComment } from '../actions/comments';
import { useSnackbar } from 'notistack';

const Question = ({ user, nextEval, lang, pointsPerStatement, params, error, surid, ...props }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { eval: evalId, page: pageParam } = params;

  const {
    answers,
    comments,
    availablePoints,
    spentPoints,
    customQuestions,
    customTextAnswers,
    enabledSliderLabels,
    indexOfTheLastPage,
    labels,
    judgemental,
    modal,
    modalFade,
    qFade,
    nextLink,
    scaleRange,
    statements,
    textAnswer,
    sendingLoading,
    page,
    evaluatee,
    competenciesCommentRequired
  } = useSelector(state => {
    const page = parseInt(pageParam, 10);
    const labels = state.labels;

    const comments = !state.pages[page]
      ? []
      : state.pages[page].map(
          pageStatement =>
            state.comments.find(
              comment =>
                comment.whom === evalId &&
                comment.tsn_statement_id.toString() === pageStatement.toString(),
            ) || {
              who: user,
              whom: evalId,
              tsn_statement_id: pageStatement.toString(),
              content: '',
            },
        );

    let answers = [];

    if (state.answers[evalId] && state.pages[page] && state.answers[evalId][state.pages[page][0]]) {
      const answersObject = state.answers[evalId];
      answers = state.pages[page].map(statement_id => answersObject[statement_id]);
    } else if (state.pages[page]) {
      answers = state.pages[page].map(statement_id => {
        return {
          statement_id,
          value: labels[0].value,
          lock: 'unlock',
          enabled: true,
        };
      });
    }

    const nextPage = page + 1;
    let nextLink;

    if (nextPage < state.indexOfTheLastPage + 1 + state.customQuestions.length) {
      nextLink = `/q/${evalId}/${nextPage}`;
    } else if (nextEval.id === 'end') {
      nextLink = `/${user}/end`;
    } else {
      nextLink = `/q/${nextEval.id}/1`;
    }

    return {
      answers,
      comments,
      labels,
      page,
      nextLink,
      availablePoints: getAvailablePoints(state, page),
      spentPoints: getSpentPoints(state, evalId, page),
      statements: state.statements,
      scaleRange: state.scaleRange,
      customQuestions: state.customQuestions,
      customTextAnswers: state.customTextAnswers[evalId] || {},
      textAnswer: state.textAnswers[evalId] || { text: '', check: false },
      evaluatee: state.evals[evalId],
      modal: state.modal,
      sendingLoading: state.animation.sendingLoading || '',
      qFade: state.animation.question || '',
      modalFade: state.animation.modal || 'animated fadeIn',
      judgemental: state.judgemental,
      enabledSliderLabels: state.enabledSliderLabels,
      indexOfTheLastPage: state.indexOfTheLastPage, //Last page of the slider (aka 360) questions
      competenciesCommentRequired: state.competenciesCommentRequired
    };
  });
  const indexOfTheLastSurveyPage = indexOfTheLastPage + customQuestions.length;

  const [bounce, setBounce] = useState('');
  const [clickedOn, setClickedOn] = useState(false);
  const [warningSnackbarKey, setWarningSnackbarKey] = useState('');
  const [errorSnackbarKey, setErrorSnackbarKey] = useState('');
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [currentComment, setCurrentComment] = useState({});

  const remainingPoints = availablePoints - spentPoints;
  const actualCustomTextQuestionIndex =
    page <= indexOfTheLastSurveyPage && page > indexOfTheLastPage - 1
      ? page - indexOfTheLastPage
      : null;

  useEffect(() => {
    scrollTop(600);
    setBounce('animated bounceIn');
    setTimeout(() => setBounce(''), 500);
  }, [evalId]);

  useEffect(() => {
    closeSnackbar(errorSnackbarKey);
  }, [spentPoints]);

  const handleNext = async () => {
    const linkToNextPage =
      page === indexOfTheLastSurveyPage && nextLink.indexOf('end') === -1 ? null : nextLink;

    if (judgemental && !(remainingPoints === 0)) {
      const key = enqueueSnackbar(lang.q360_q_remaining_points_warning, {
        variant: 'error',
      });
      setErrorSnackbarKey(key);
      return;
    }

    if(competenciesCommentRequired &&  comments.some(({content}) => !content)) {
      const key = enqueueSnackbar(lang.q360_q_missing_competency_feedback, {
        variant: 'error',
      });
      setErrorSnackbarKey(key);
      return;
    }

    if (answers.some(answer => answer.value === labels[0].value) && !clickedOn) {
      const key = enqueueSnackbar(lang.q360_q_zero_value_statement, { variant: 'warning' });
      setWarningSnackbarKey(key);
      setClickedOn(true);
      return;
    }

    if (clickedOn) {
      closeSnackbar(warningSnackbarKey);
      setClickedOn(false);
    }

    if (page >= indexOfTheLastPage) {
      if (page >= indexOfTheLastPage - 1 && page < indexOfTheLastSurveyPage) {
        const questionId = (customQuestions[actualCustomTextQuestionIndex] || {}).id;
        if (!customTextAnswers[questionId]) {
          dispatch(textAnswerError(lang.q_no_answer_warning_text));
        } else {
          dispatch(
            postCustomTextAnswers(
              {
                who: user,
                whom: evaluatee.id,
                tsn_survey_id: surid,
                question_id: questionId,
                text: customTextAnswers[questionId].check
                  ? null
                  : customTextAnswers[questionId].text,
                check: customTextAnswers[questionId].check || null,
              },
              linkToNextPage,
            ),
          );
        }
      } else {
        if (textAnswer.text.length === 0 && !textAnswer.check) {
          dispatch(textAnswerError(lang.q_no_answer_warning_text));
        } else {
          dispatch(
            postTextAnswers(
              {
                who: user,
                whom: evaluatee.id,
                tsn_survey_id: surid,
                text_answer: textAnswer.check ? null : textAnswer.text,
              },
              linkToNextPage,
              () => dispatch(showStatusModal()),
            ),
          );
        }
      }
    } else {
      dispatch(saveAnswers(answers, evaluatee.id));
      dispatch(
        postAnswers(
          {
            who: user,
            whom: evaluatee.id,
            tsn_survey_id: surid,
            answers: answers.map(a => {
              return {
                statement_id: a.statement_id,
                value: a.value,
                enabled: a.enabled,
              };
            }),
            comments: comments,
          },
          nextLink,
        ),
      );
    }
    window.scrollTo(0, 0)
  };

  const findComment = (comments, statement_id, who, whom) => {
    const result = comments.find(
      comment =>
        comment.tsn_statement_id.toString() === statement_id.toString() && comment.whom === whom,
    );
    return (
      result || {
        who,
        whom,
        tsn_statement_id: statement_id.toString(),
        content: '',
      }
    );
  };

  let nextText = lang.q360_q_next;
  let nextClass = 'nav-button';

  if (page === indexOfTheLastSurveyPage) {
    nextText = lang.q360_save_and_continue;
    nextClass = 'nav-button-last-next';
  }

  let instructions =
    pointsPerStatement !== scaleRange ? lang.q360_q_instructions : lang.q360_q_scaleInstructions;

  let customQuestionId = null;

  if (page < indexOfTheLastSurveyPage && page > indexOfTheLastPage - 1) {
    // black magic here. this is the text of custom question also.
    // move this to the sliderQuestion, textQuestion, etc... modules LATERó
    customQuestionId = (customQuestions[actualCustomTextQuestionIndex] || {}).id;
    instructions = (customQuestions[actualCustomTextQuestionIndex] || {}).questionText;
  } else if (page === indexOfTheLastSurveyPage) {
    if (user === evaluatee.id) {
      instructions = lang.q360_q_instructions_text_selfevaluation;
    } else {
      instructions = lang.q360_q_instructions_text;
    }
  }

  return (
    <div className='question animated fadeIn'>
      {modal && (
        <StatusModal
          modalFade={modalFade}
          lang={lang}
          status={props.status}
          pointsPerStatement={pointsPerStatement}
        />
      )}
      <div className='col-md-12 col-sm-12 col-xs-12'>
        <div className='col-md-7 col-md-offset-1 eval-status'>
          {lang.infoAboutLangFile.lang === 'en' && lang.q360_q_person_status}
          <div className={`eval-name ${bounce}`}>{` ${evaluatee.name}`}</div>
          {lang.infoAboutLangFile.lang === 'hu' && lang.q360_q_person_status}: {page}/
          {indexOfTheLastSurveyPage}
        </div>
      </div>
      <div className='col-md-8 col-md-offset-2 col-sm-12 col-xs-12 center mar20'>
        <h3>{instructions}</h3>
        <Sticky enabled top={0} innerZ={1} activeClass='sticked'>
          <div>
            {pointsPerStatement !== scaleRange && (
              <div className={`budget ${page >= indexOfTheLastPage ? 'hidden' : ''}`}>
                <img src={`../../${iconOk}`} alt='remaining-points' />
                <div className='amount'>{remainingPoints.toFixed(1)}</div>
              </div>
            )}
          </div>
        </Sticky>
        {isCommentModalOpen && (
          <Comment
            lang={lang}
            comment={findComment(comments, currentComment.statement_id, user, currentComment.whom)}
            saveComment={comment => dispatch(saveComment(comment))}
            handleCloseComment={() => {
              setIsCommentModalOpen(false);
              setCurrentComment({});
            }}
            statement={statements[currentComment.statement_id]}
          />
        )}
      </div>
      <div className='col-md-12 col-sm-12 col-xs-12'>
        <Loading size={5} cssClass={`sending-loader ${sendingLoading}`} />
      </div>
      <div className={`q-animated ${qFade}`}>
        {page < indexOfTheLastPage && page > 0 && (
          <SliderQuestion
            availablePoints={availablePoints}
            scaleRange={scaleRange}
            labels={labels}
            statements={statements}
            answers={answers}
            lang={lang}
            eval={evaluatee}
            enabledSliderLabels={enabledSliderLabels}
            handleOpenComment={({ statement_id, whom }) => {
              setIsCommentModalOpen(true);
              setCurrentComment({ statement_id, whom });
            }}
            comments={comments}
            remainingPoints={remainingPoints}
          />
        )}
        {page < indexOfTheLastSurveyPage && page > indexOfTheLastPage - 1 && (
          <CustomTextQuestion
            eval={evaluatee}
            customQuestionId={customQuestionId}
            customTextChanged={(value, evalId, questionId) =>
              dispatch(customTextChanged(value, evalId, questionId))
            }
            customTextCheckboxChanged={(value, evalId, questionId) =>
              dispatch(customTextCheckboxChanged(value, evalId, questionId))
            }
            customTextAnswers={customTextAnswers}
            lang={lang}
          />
        )}
        {page === indexOfTheLastSurveyPage && (
          <TextQuestion
            eval={evaluatee}
            textChanged={(value, evalId) => dispatch(textChanged(value, evalId))}
            checkboxChanged={(value, evalId) => dispatch(checkboxChanged(value, evalId))}
            textAnswer={textAnswer}
            lang={lang}
          />
        )}
        <div className='col-md-12 col-sm-12 col-xs-12 center mar40 pad60-bottom'>
          <NavButtons
            handleNext={handleNext}
            nextText={nextText}
            nextClass={nextClass}
            handleBack={() => {
              props.goBack();
              setTimeout(browserHistory.goBack, 600);
            }}
            backText={lang.q360_q_back}
          />
        </div>
      </div>
      {error && error.save && <Error msg={error.save} />}
      {error && error.textAnswer && <Error msg={error.textAnswer} />}
    </div>
  );
};

export default Question;
